<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <div class="row">
        <div class="col-md-12 component-title-row">
          <h3><i class="fa fa-fw fa-cube"></i> {{ _('Resources') }}</h3>
          <div class="component-title-buttons">
            <button class="newSelector btn btn-success addbutton" type="button" v-on:click="createNewDialog"><i class="fa fa-fw fa-plus"></i><span class="hidden-xs"> {{ _('Create new...') }}</span></button>
          </div>
        </div>
      </div>
      <div class="list-filter-row">
        <div class="list-filter-col">
          <div class="form-group">
            <div class="input-group">
              <input class="form-control" id="searchField" type="text" v-model="search" :placeholder="_('Keyword search')" />
              <span class="input-group-btn">
                <button type="button" class="btn btn-success"><i class="fa fa-fw fa-search"></i></button>
              </span>
            </div>
          </div>
        </div>
        <div class="list-filter-col" v-if="displayedTypes !== 'buyable'">
          <div class="form-group">
            <div class="input-group">
              <div class="input-group-addon">{{ _('Display') }}</div>
              <select class="form-control" id="resourceTypeFilter" v-model="type">
                <option value="all">{{ _('All resources') }}</option>
                <option value="allreservable">{{ _('Reservable resources') }}</option>
                <option value="buyable" v-if="displayedTypes !== 'reservable'">{{ _('Stock resources') }}</option>
                <option value="openinghours">{{ _('Opening hours') }}</option>
                <option value="orphaned">{{ _('Orphaned resources') }}</option>
                <option value="resourcepool">{{ _('Resource pools') }}</option>
              </select>
            </div>
          </div>
        </div>
        <div class="list-filter-col" v-if="showFilters">
          <div class="form-group">
            <div class="input-group">
              <div class="input-group-addon">{{ _('Filter') }}</div>
              <select class="form-control" id="categoryFilter" v-model="category">
                <option value="all">{{ _('No filter') }}</option>
                <optgroup :label="_('By category')" v-if="categories && categories.length">
                  <option v-for="category in categories" :value="category.id">{{ category.name }}</option>
                </optgroup>
                <optgroup :label="_('By resource pool')" v-if="resourcePools && resourcePools.length">
                  <option v-for="resourcePool in resourcePools" :value="resourcePool.id">{{ resourcePool.name }}</option>
                </optgroup>
              </select>
            </div>
          </div>
        </div>
        <div class="button-col">
          <button class="btn btn-default" type="button" v-on:click="toggleMultiSelect"><template v-if="tableOptions.selectable"><i class="fa fa-fw fa-window-close"></i><span class="hidden-xs"> {{ _('Cancel') }}</span></template><template v-else><i class="fa fa-fw fa-check-square"></i><span class="hidden-xs"> {{ _('Select') }}</span></template></button>
        </div>
      </div>
      <div class="list-filter-row space-between" v-if="tableOptions.selectable">
        <div class="button-col">
          <button class="btn btn-danger" type="button" v-on:click="removeSelectedDialog" :disabled="!canDelete"><i class="fa fa-fw fa-trash"></i> {{ _('Delete selected') }}</button>
          <button class="btn btn-success" type="button" v-on:click="modifySelectedDialog" :disabled="!canModifyMultiple"><i class="fa fa-fw fa-pencil"></i> {{ _('Edit selected') }}</button>
        </div>
        <button class="btn btn-success" type="button" v-on:click="downloadXlsx"><i class="fa fa-fw fa-download"></i> {{ _('Download Excel file') }}</button>
      </div>
      <Datatable :url="resourcesUrl" :open-groups="openResourcepools" :search="search" :displayed-type-filter="type" :category-filter="category" :options="tableOptions" ref="datatable" v-on:data="tableEvent" v-on:selection="rowsSelected" v-on:edited="cellEdited" v-on:editing="cellEditing" v-on:editcancelled="cellEditCancelled" />
    </div>
  </div>
</template>

<script>

import axios from 'axios'
import components from '~/lib/components'
import { ResourceTypeSelector } from '#components'
// import ResourceTypeSelector from '~/components/modals/ResourceTypeSelector'
import formatters from '~/lib/formatters'
import resourcetypes from '~/lib/resourcetypes'
import { mapActions, mapGetters } from 'vuex'
// import Datatable from '~/components/Datatable'
import debounce from 'lodash/debounce'
import memoize from 'lodash/memoize'

export default {
  data () {
    let $this = this
    const _ = useNuxtApp().$translate
    return {
      resources: [],
      selectedResources: [],
      search: '',
      type: 'all',
      pool: '',
      idCellClicked: false,
      cellEditorActive: false,
      activeRow: false,
      reloader: null,
      batchCreationInProgress: false,
      resourcePools: [],
      category: null,
      categories: [],
      tableOptions: {
        persistenceSuffix: $this.component.id, 
        dataTree: true,
        selectable: false,
        parentType: 'resourcepool',
        parentField: 'id',
        responsiveLayoutCollapseFormatter: $this.collapseFormatter(),
        columns: [
        {
          title: 'ID',
          field: 'id',
          width: 90,
          cellClick (e, cell) {
            $this.idCellClicked = true
          },
          responsive: 0
        },
        {
          title: _('Name'),
          field: 'name',
          widthGrow: 5,
          formatter: $this.resourceNameFormatter(),
          responsive: 0
        },
        {
          title: _('Type'),
          field: 'displayedtype',
          widthGrow: 4,
          formatter: $this.resourceTypeFormatter()
        },
        {
          title: _('Capacity') + ' / ' + _('Stock balance'),
          field: 'capacity',
          widthGrow: 3,
          formatter: $this.resourceCapacityFormatter(),
          editor: 'number',
          editorParams: {
            min: 0,
            verticalNavigation: 'table'
          },
          // Seat map resources derive their capacity from the actual seat count
          // (kept in sync by the backend on save) - editing it here directly
          // would just get silently overwritten on the next seat map save.
          editable (cell) {
            return !cell.getData().seatmap_id
          }
        },
        ],
        initialSort: [{
          column: 'displayedtype',
          dir: 'asc'
        }],
        rowClick (e, row) {
          if (!$this.tableOptions.selectable && !$this.idCellClicked) {
            if (!$this.cellEditorActive || row.getIndex() !== $this.activeRow) {
              $this.editRow(row)
              $this.cellEditorActive = false
              $this.activeRow = false
            }
          }
          $this.idCellClicked = false
        },
        dataTreeRowExpanded (row, level) {
          $this.setOpenResourcepools([...$this.openResourcepools.filter(c => c.id !== row.getData().id), { id: row.getData().id, name: row.getData().name, level: level }])
        },
        dataTreeRowCollapsed (row, level) {
          $this.setOpenResourcepools($this.openResourcepools.filter(c => c.id !== row.getData().id))
        }
      }
    }
  },
  mixins: [components, formatters, resourcetypes],
  computed: {
    ...mapGetters(['openResourcepools']),
    data () {
      return JSON.parse(this.component.data)
    },
    displayedTypes () {
      if (!this.data || !this.data.types) {
        return 'all'
      }
      return this.data.types
    },
    showFilters () {
      return this.categories && this.categories.length
    },
    resourcesUrl () {
      return this.baseDomain + this.shopId + '/groupedresources.json?apikey=' + this.apiKey + '&types=' + this.displayedTypes
    },
    canDelete () {
      return this.selectedResources && this.selectedResources.length > 0
    },
    canModifyMultiple () {
      if (this.canDelete) {
        const types = [...new Set(this.selectedResources.map(r => r.displayedtype))]
        if (types.includes('openinghours')) {
          return false
        }
        if (types.length > 1 && types.includes('buyable')) {
          return false
        }
        return true
      }
      return false
    }
  },
  methods: {
    ...mapActions([
      'setOpenResourcepools'
    ]),
    cellEditing (cell) {
      this.cellEditorActive = true
      this.activeRow = cell.getRow().getIndex()
    },
    cellEditCancelled (cell) {
      this.cellEditorActive = false
      this.activeRow = false
    },
    async cellEdited (cell) {
      this.cellEditorActive = false
      this.activeRow = false
      const val = cell.getValue()
      const oldVal = cell.getOldValue()
      const row = cell.getRow()
      const full = row.getData()
      switch(full.displayedtype) {
        case 'resourcepool':
        case 'openinghours':
          cell.restoreOldValue()
          return
      }
      const data = {
        id: full.id,
        count: val,
        originalcount: oldVal
      }
      try {
        await axios.patch(this.baseDomain + this.shopId + '/resources/' + full.id + '.json?apikey=' + this.apiKey, data)
      } catch(e) {
        this.showError(e)
        this.restoreOldValue()
      }
    },
    downloadXlsx () {
      const filters = []
      const selectedIds = this.selectedResources.filter(r => r.displayedtype !== 'resourcepool').map(r => r.id)
      if (selectedIds.length) {
        filters.push({ field: 'search', type: 'like', value: selectedIds.join(',') })
      } else {
        if (this.search) {
          filters.push({ field: 'search', type: 'like', value: this.search })
        }
        // Resource pools are not exported, so that view exports all resources
        if (this.type && this.type !== 'all' && this.type !== 'resourcepool') {
          filters.push({ field: 'displayedtype', type: '=', value: this.type })
        }
      }
      let filterStr = ''
      if (filters.length) {
        filterStr = '&filters[]=' + filters.map(f => encodeURIComponent(JSON.stringify(f))).join('&filters[]=')
      }
      const sorters = '&sorters[]=' + encodeURIComponent(JSON.stringify({ field: 'name', dir: 'asc' }))
      window.open(this.baseDomain + this.shopId + '/resources.xlsx?apikey=' + this.apiKey + '&types=' + this.displayedTypes + sorters + filterStr)
    },
    rowsSelected (data) {
      this.selectedResources = data
    },
    filterClass (context) {
      if (this.showFilters) {
        if (this.columnWidth === 6) {
          return 'col-md-6'
        }
        return 'col-md-3 col-sm-6'
      } else {
        return 'col-md-4 col-sm-4 col-xs-12'
      }
    },
    filterAddonClass (context) {
      if (this.showFilters) {
        return 'input-group-addon hidden-md'
      } else {
        return 'input-group-addon hidden-md' + (this.columnWidth === 6 ? ' hidden-lg' : '')
      }
    },
    removeSelectedDialog () {
      this.showDialog({
        title: this._('Are you sure? Selected resources will be deleted permanently.'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Delete'),
            class: 'danger',
            icon: 'trash',
            handler: this.removeSelected
          }
        ]
      })
    },
    modifySelectedDialog () {
      this.showModal('resourcemodifier', ResourceTypeSelector, {
        props: {
          selectedResources: this.selectedResources
        },
        events: {
          selected: (data) => {
            this.modifyMultiple(data)
          }
        },
        handlers: {},
        width: 800
      })
    },
    removeSelected () {
      for (let i = 0; i < this.selectedResources.length; i++) {
        let id = this.selectedResources[i].id
        this.remove(id)
      }
      this.selectedResources = []
      this.tableOptions.selectable = false
    },
    async remove (id) {
      try {
        await axios.delete(this.baseDomain + this.shopId + '/resources/' + id + '.json?apikey=' + this.apiKey)
      } catch(e) {
        return this.showError(e)
      }
    },
    editRow (row) {
      let id = row.getCell('id').getValue()
      let type = row.getCell('displayedtype').getValue()
      let name = row.getCell('name').getValue()
      if (type === 'resourcepool') {
        this.editResourcepool(id, name)
      } else {
        this.editResource(id, name)
      }
    },
    editResource (id, name, activeTab, skipState) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'ResourceDetails',
        title: this._('Resource') + ' ' + id + (name ? ': ' + name : ''),
        width: 6,
        data: {
          resourceId: id,
          activeTab
        },
        skipState
      })
    },
    editResourcepool (id, name, activeTab, skipState) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'ResourcepoolDetails',
        title: this._('Resource pool') + ' ' + id + (name ? ': ' + name : ''),
        width: 6,
        data: {
          resourcepoolId: id,
          activeTab
        },
        skipState
      })
    },
    async createNewDialog () {
      this.showModal('resourcetypeselector', ResourceTypeSelector, {
        props: {
          openResourcepools: this.openResourcepools
        },
        events: {
          selected: (data) => {
            if (data.multiply) {
              this.createMultiple(data)
            } else {
              this.createNew(data)
            }
          }
        },
        handlers: {},
        width: 800
      })
    },
    numberToLetter(i) {
      const baseChar = ('A').charCodeAt(0)
      let letters  = ''
      while (i >= 0) {
        letters = String.fromCharCode(i % 26 + baseChar) + letters
        i = Math.floor(i / 26) - 1
      }
      return letters
    },
    charToNumber(char) {
      return char.toUpperCase().charCodeAt(0) - 65
    },
    getItemName(baseName, rules, i) {
      let start
      if (rules.naming && rules.naming.match(/num/)) {
        start = parseInt(rules.namingStart, 10)
        if (!start) {
          start = 1
        }
      } else if (rules.naming && rules.naming.match(/char/)) {
        start = rules.namingStart
        if (!start || !start.match(/^[A-Z]$/)) {
          start = 'A'
        }
        start = this.charToNumber(start)
      }
      switch (rules.naming) {
        case 'addnum':
          return baseName + ' ' + (i + start)
        case 'addchar':
          return baseName + ' ' + this.numberToLetter(i + start)
        case 'replacenum':
          return baseName.replace(/(\d+)(?!.*\d)+/, (i + start).toString())
        case 'replacechar':
          return baseName.replace(/(^|\s)([\p{L}\p{M}])(?=$|\s)/u, (match, p1, p2) => p1 + this.numberToLetter(i + start)) // return baseName.replace(/(?<=^|\s)[\p{L}\p{M}](?=$|\s)/u, this.numberToLetter(i + start)) <- this version does not work in old Safari
        case 'list':
          const nameList = rules.nameList.split('\n')
          if (i < nameList.length) {
            return nameList[i]
          } else {
            return nameList[nameList.length - 1] + ' ' + (i - nameList.length + 2)
          }
        default:
          return baseName
      }
    },
    async modifyMultiple (data) {
      this.$socket.emit('message', {
        type: 'batchResourceUpdateBegin',
        shopId: this.shopId
      })
      const baseName = data.multiply.resource.name
      for (let i = 0; i < this.selectedResources.length; i++) {
        let { data: current } = await axios.get(this.baseDomain + this.shopId + '/resources/' + this.selectedResources[i].id + '.json?ranges=true&notifications=true&apikey=' + this.apiKey)
        if (baseName) {
          data.multiply.resource.name = this.getItemName(baseName, data.multiply, i)
        } else if (data.multiply.naming !== 'none') {
          data.multiply.resource.name = this.getItemName(current.name, data.multiply, i)
        }
        await this.modifyResource(current, data.multiply)
      }
      this.$socket.emit('message', {
        type: 'batchResourceUpdateEnd',
        shopId: this.shopId
      })
      this.reload()
      this.toggleMultiSelect()
    },
    async modifyResource(current, multiply) {
      const attrs = { ...multiply.resource }
      // Check if replaceRanges is 0, if so, append attrs.ownRange to current.ownRange
      if (multiply.replaceRanges === 0 && attrs.ownRange && current.ownRange) {
        attrs.ownRange = [...current.ownRange, ...attrs.ownRange]
      }
      // Check if replaceNotifications is 0, if so, append attrs.ownNotification to current.ownNotification
      if (multiply.replaceNotifications === 0 && attrs.ownNotification && current.ownNotification) {
        attrs.ownNotification = [...current.ownNotification, ...attrs.ownNotification]
      }
      try {
        let data = { ...current, ...attrs }
        if (current.displayedtype === 'buyable') {
          if (attrs.capacity) {
            data.count = attrs.capacity
          }
          data.originalcount = current.count
        }
        await axios.put(this.baseDomain + this.shopId + '/resources/' + current.id + '.json?apikey=' + this.apiKey, data)
      } catch(e) {
        console.log('error', e)
      }
    },
    async createMultiple (data) {
      this.$socket.emit('message', {
        type: 'batchResourceUpdateBegin',
        shopId: this.shopId
      })
      if (data.selectedResourcepool === 'new') {
        let response
        let pd = {
          name: data.newResourcepoolName ? data.newResourcepoolName : this._('Resource pool'),
          description: ''
        }
        try {
          response = await axios.post(this.baseDomain + this.shopId + '/resourcepools.json?apikey=' + this.apiKey, pd)
          data.selectedResourcepool = response.data
        } catch(e) {
          // Fail silently and just place the item into root
          data.selectedResourcepool = null
        }
      }
      for (let i = 0; i < data.multiply.count; i++) {
        const base = {
          name: this.getItemName(data.name, data.multiply, i),
          selectedResourcepool: data.selectedResourcepool,
          selectedType: data.selectedType,
          newResourcepoolName: data.newResourcepoolName
        }
        const attrs = data.multiply.resource ? data.multiply.resource : data.multiply.resourcepool
        await this.createNew(base, attrs)
      }
      this.$socket.emit('message', {
        type: 'batchResourceUpdateEnd',
        shopId: this.shopId
      })
      this.reload()
    },
    async createNew (data, attrs) {
      let response
      let d = {
        name: data.name,
        description: ''
      }
      if (data.selectedResourcepool) {
        // This seems redundant, but is not: createMultiple should only create one resource pool, but createNew can be called by itself
        // This could be extracted into a function though
        if (data.selectedResourcepool === 'new') {
          let pd = {
            name: data.newResourcepoolName ? data.newResourcepoolName : this._('Resource pool'),
            description: ''
          }
          try {
            response = await axios.post(this.baseDomain + this.shopId + '/resourcepools.json?apikey=' + this.apiKey, pd)
            d.resourcepool = response.data
          } catch(e) {
            // Fail silently and just place the item into root
            d.resourcepool = null
          }
        } else {
          d.resourcepool = data.selectedResourcepool
        }
      }
      try {
        if (data.selectedType === 'resourcepool') {
          response = await axios.post(this.baseDomain + this.shopId + '/resourcepools.json?apikey=' + this.apiKey, d)
          if (response.data && !attrs) {
            this.editResourcepool(response.data, data.name, 'general')
          }
        } else {
          if (data.selectedType !== 'openinghours' && !attrs) {
            d.count = 1
            d.mincount = 1
          }
          if (data.selectedType === 'resource') {
            d.displayedtype = 'generic'
          } else {
            d.displayedtype = data.selectedType
          }
          response = await axios.post(this.baseDomain + this.shopId + '/resources.json?apikey=' + this.apiKey, d)
          if (response.data) {
            if (attrs) {
              attrs.id = response.data
              if (attrs.name) { // Let's not overwrite our generated name
                attrs.name = d.name
              }
              if (attrs.id) {
                await axios.put(this.baseDomain + this.shopId + '/resources/' + attrs.id + '.json?apikey=' + this.apiKey, attrs)
              }
            } else {
              this.editResource(response.data, data.name, 'general')
            }
          }
        }
      } catch(e) {}
    },
    async loadCategories () {
      const params = {
        sorters: [{
          field: 'name',
          dir: 'asc'
        }]
      }
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/categories.json', { params })
      } catch(e) {
        return
      }
      this.categories = response.data
    },
    async loadResourcepools() {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/resourcepools.json?iSortingCols=1&iSortCol_0=1&sSortDir_0=asc&apikey=' + this.apiKey)
      } catch(e) {
        return
      }
      this.resourcePools = response.data
    },
    load (message) {
      this.$refs.datatable.reload(false, message)
    },
    reload (message) {
      this.reloader(message)
    },
    memoizeDebounce: function(func, wait = 0, options = {}) {
      var mem = memoize(function() {
        return debounce(func, wait, options)
      }, options.resolver)
      return function() { mem.apply(this, arguments).apply(this, arguments) }
    },
  },
  mounted () {
    this.loadCategories()
    // No need to load these since they are displayed at the top level
    // this.loadResourcepools()
    this.reloader = this.memoizeDebounce(this.load, 500, { resolver: (message) => message ? message.shopId + '-' + message.type + '-' + message.data ? message.data.id : '*' : '*' })
    if(!this.events) {
      let events = {}
      events['batchResourceUpdateBegin'] = (message) => {
        this.batchCreationInProgress = true
      }
      events['batchResourceUpdateEnd'] = (message) => {
        this.batchCreationInProgress = false
      }
      events['resourceUpdate'] = (message) => {
        if (this.batchCreationInProgress) {
          return
        }
        if (!this.cellEditorActive) {
          return this.reload(message)
        } else {
          return this.$refs.datatable.setNewData()
        }
      }
      events['resourcepoolUpdate'] = (message) => {
        if (this.batchCreationInProgress) {
          return
        }
        if (!this.cellEditorActive) {
          return this.reload(message)
        } else {
          return this.$refs.datatable.setNewData()
        }
      }
      events['restoreDetails'] = (message) => {
        if (message.shopId !== this.shopId) {
          return
        }
        switch(message.component) {
          case 'ResourceDetails':
            this.editResource(message.data.resourceId, null, null, true)
            break
          case 'ResourcepoolDetails':
            this.editResourcepool(message.data.resourcepoolId, null, null, true)
            break
        }
      }
      this.events = events
    }
    this.enableEventBus()
  }
}
</script>